// how frequently a data point is plotted
export const PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS = {
  DAILY: "day",
  WEEKLY: "week",
  MONTHLY: "month",
} as const;

export const PERFORMANCE_DASHBOARD_TIME_FREQUENCY_LABELS = {
  [PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS.DAILY]: "Daily",
  [PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS.WEEKLY]: "Weekly",
  [PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS.MONTHLY]: "Monthly",
} as const;

export const PERFORMANCE_DASHBOARD_DAILY_PERIOD_OPTIONS = {
  SEVEN_DAYS: "seven_days",
  FOURTEEN_DAYS: "fourteen_days",
  THIRTY_DAYS: "thirty_days",
  SIXTY_DAYS: "sixty_days",
} as const;

export const PERFORMANCE_DASHBOARD_WEEKLY_PERIOD_OPTIONS = {
  FOUR_WEEKS: "four_weeks",
  EIGHT_WEEKS: "eight_weeks",
  TWELVE_WEEKS: "twelve_weeks",
  TWENTY_FOUR_WEEKS: "twenty_four_weeks",
} as const;

export const PERFORMANCE_DASHBOARD_MONTHLY_PERIOD_OPTIONS = {
  THREE_MONTHS: "three_months",
  SIX_MONTHS: "six_months",
  NINE_MONTHS: "nine_months",
  TWELVE_MONTHS: "twelve_months",
} as const;

// the total time period for which the graph spans over.
// the time period options are different for each time frequency option that is selected.
export const PERFORMANCE_DASHBOARD_TIME_PERIOD_OPTIONS_MAP = {
  [PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS.DAILY]:
    PERFORMANCE_DASHBOARD_DAILY_PERIOD_OPTIONS,
  [PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS.WEEKLY]:
    PERFORMANCE_DASHBOARD_WEEKLY_PERIOD_OPTIONS,
  [PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS.MONTHLY]:
    PERFORMANCE_DASHBOARD_MONTHLY_PERIOD_OPTIONS,
} as const;

export const PERFORMANCE_DASHBOARD_TIME_PERIODS = {
  ...PERFORMANCE_DASHBOARD_DAILY_PERIOD_OPTIONS,
  ...PERFORMANCE_DASHBOARD_WEEKLY_PERIOD_OPTIONS,
  ...PERFORMANCE_DASHBOARD_MONTHLY_PERIOD_OPTIONS,
} as const;

// unique data for each time period option.
// we use the days for each option so we have a consistent unit of time to do calculations with.
export const PERFORMANCE_DASHBOARD_TIME_PERIOD_DATA = {
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.SEVEN_DAYS]: {
    label: "7d",
    days: 7,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.FOURTEEN_DAYS]: {
    label: "14d",
    days: 14,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.THIRTY_DAYS]: {
    label: "30d",
    days: 30,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.SIXTY_DAYS]: {
    label: "60d",
    days: 60,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.FOUR_WEEKS]: {
    label: "4w",
    days: 28,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.EIGHT_WEEKS]: {
    label: "8w",
    days: 56,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.TWELVE_WEEKS]: {
    label: "12w",
    days: 84,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.TWENTY_FOUR_WEEKS]: {
    label: "24w",
    days: 168,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.THREE_MONTHS]: {
    label: "3m",
    days: 90,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.SIX_MONTHS]: {
    label: "6m",
    days: 180,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.NINE_MONTHS]: {
    label: "9m",
    days: 270,
  },
  [PERFORMANCE_DASHBOARD_TIME_PERIODS.TWELVE_MONTHS]: {
    label: "12m",
    days: 360,
  },
} as const;

export const PERFORMANCE_CLOSER_TIERS = {
  ACTIVE: "active",
  PREFERRED: "preferred",
  SILVER: "silver",
  GOLD: "gold",
  DIAMOND: "diamond",
  PRESIDENTS: "presidents",
};

export const PERFORMANCE_CLOSER_TIER_LABELS = {
  [PERFORMANCE_CLOSER_TIERS.ACTIVE]: "Active",
  [PERFORMANCE_CLOSER_TIERS.PREFERRED]: "Preferred",
  [PERFORMANCE_CLOSER_TIERS.SILVER]: "Silver",
  [PERFORMANCE_CLOSER_TIERS.GOLD]: "Gold",
  [PERFORMANCE_CLOSER_TIERS.DIAMOND]: "Diamond",
  [PERFORMANCE_CLOSER_TIERS.PRESIDENTS]: "Presidents",
};

// It's exclusively frontend metric, so don't add it to the PERFORMANCE_CLOSER_TIER_LABELS
export const NEWBIE_CALLER_TIER = "Newbie";

export const PERFORMANCE_CHART_DATASETS_TIME_ON_DIALER = {
  time_on_dialer: "time_on_dialer",
} as const;

export const PERFORMANCE_CHART_DATASETS_TIME_ON_DIALER_LABELS = {
  [PERFORMANCE_CHART_DATASETS_TIME_ON_DIALER.time_on_dialer]: "Time on dialer",
} as const;

export const PERFORMANCE_CHART_DATASETS_CALLS = {
  pickups: "pickups",
  conversations: "conversations",
  convo_right_icp: "convo_right_icp",
  booked_meetings: "booked_meetings",
  prequalified_meetings: "prequalified_meetings",
} as const;

export const PERFORMANCE_CHART_DATASETS_CALLS_LABELS = {
  [PERFORMANCE_CHART_DATASETS_CALLS.pickups]: "Pickups",
  [PERFORMANCE_CHART_DATASETS_CALLS.conversations]: "Conversations",
  [PERFORMANCE_CHART_DATASETS_CALLS.convo_right_icp]: "Convo right ICP",
  [PERFORMANCE_CHART_DATASETS_CALLS.booked_meetings]: "Booked meetings",
  [PERFORMANCE_CHART_DATASETS_CALLS.prequalified_meetings]:
    "Pre-qualified meetings",
} as const;

export const PERFORMANCE_CHART_DATASETS_MISC_METRICS = {
  dials: "dials",
  bad_data_convo: "bad_data_convo",
  convo_wrong_icp: "convo_wrong_icp",
} as const;

export const PERFORMANCE_CHART_DATASETS_MISC_METRICS_LABELS = {
  [PERFORMANCE_CHART_DATASETS_MISC_METRICS.dials]: "Dials",
  [PERFORMANCE_CHART_DATASETS_MISC_METRICS.bad_data_convo]: "Bad data convo",
  [PERFORMANCE_CHART_DATASETS_MISC_METRICS.convo_wrong_icp]: "Convo wrong ICP",
} as const;

export const PERFORMANCE_CHART_DATASETS_MEETINGS = {
  rescheduled_meetings: "rescheduled_meetings",
  canceled_meetings: "canceled_meetings",
  no_show_meetings: "no_show_meetings",
  unqualified_meetings: "unqualified_meetings",
  qualified_meetings: "qualified_meetings",
} as const;

export const PERFORMANCE_CHART_DATASETS_MEETINGS_LABELS = {
  [PERFORMANCE_CHART_DATASETS_MEETINGS.rescheduled_meetings]: "Reschedules",
  [PERFORMANCE_CHART_DATASETS_MEETINGS.canceled_meetings]: "Cancels",
  [PERFORMANCE_CHART_DATASETS_MEETINGS.no_show_meetings]: "No shows",
  [PERFORMANCE_CHART_DATASETS_MEETINGS.unqualified_meetings]:
    "Show, not qualified",
  [PERFORMANCE_CHART_DATASETS_MEETINGS.qualified_meetings]:
    "Qualified meetings",
} as const;

export const PERFORMANCE_CHART_DATA_CONFIG = {
  [PERFORMANCE_CHART_DATASETS_CALLS.prequalified_meetings]: {
    border: "#E63B9EE5",
    backgroundColor: "rgba(230, 59, 158, 0.90)",
  },
  [PERFORMANCE_CHART_DATASETS_CALLS.booked_meetings]: {
    border: "#BE84F7E5",
    backgroundColor: "rgba(190, 132, 247, 0.90)",
  },
  [PERFORMANCE_CHART_DATASETS_CALLS.conversations]: {
    border: "#FD8476E5",
    backgroundColor: "rgba(253, 132, 118, 0.90)",
  },
  [PERFORMANCE_CHART_DATASETS_CALLS.convo_right_icp]: {
    border: "#84F796",
    backgroundColor: "rgba(132, 247, 150, 0.90)",
  },
  [PERFORMANCE_CHART_DATASETS_CALLS.pickups]: {
    borderColor: "#4B88FBE5",
    backgroundColor: "rgba(75, 136, 251, 0.90)",
  },
  [PERFORMANCE_CHART_DATASETS_MEETINGS.rescheduled_meetings]: {
    borderColor: "#4B88FBE5",
    backgroundColor: "rgba(75, 136, 251, 0.90)",
  },
  [PERFORMANCE_CHART_DATASETS_MEETINGS.canceled_meetings]: {
    border: "#FD8476E5",
    backgroundColor: "rgba(253, 132, 118, 0.90)",
  },
  [PERFORMANCE_CHART_DATASETS_MEETINGS.no_show_meetings]: {
    border: "#84F796E5",
    backgroundColor: "rgba(132, 247, 150, 0.90)",
  },
  [PERFORMANCE_CHART_DATASETS_MEETINGS.unqualified_meetings]: {
    border: "#BE84F7E5",
    backgroundColor: "rgba(190, 132, 247, 0.90)",
  },
  [PERFORMANCE_CHART_DATASETS_MEETINGS.qualified_meetings]: {
    border: "#E63B9EE5",
    backgroundColor: "rgba(230, 59, 158, 0.90)",
  },
};

export const PERFORMANCE_CHART_DATA_FORMAT = {
  NUMBER: "number",
  FRACTION: "fraction",
} as const;
